import { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import ParentDashboard from './ParentDashboard';
import StaffDashboard from './StaffDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import WebsiteAdminDashboard from './dashboards/WebsiteAdminDashboard';
import AdmissionsAdminDashboard from './dashboards/AdmissionsAdminDashboard';
import PrincipalDashboard from './dashboards/PrincipalDashboard';

/**
 * LoginGateway Component
 * 
 * Handles unified login page and routes to appropriate dashboard
 * - Auto-detects user role from authentication
 * - Routes to specific dashboard based on role:
 *   - super_admin: SuperAdminDashboard
 *   - admin: WebsiteAdminDashboard
 *   - approver: PrincipalDashboard
 *   - admissions: AdmissionsAdminDashboard
 *   - parent: ParentDashboard
 * - Manages session and user state
 */

function LoginGateway({ apiBase, onBackToHome }) {
  const [userRole, setUserRole] = useState(null); // Role-based routing
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check existing session on mount
  useEffect(() => {
    checkExistingSession();
  }, []);

  /**
   * Determine user role from roles array
   * Priority: super_admin > admin > approver > admissions > editor > parent
   */
  function getUserRole(roles = []) {
    if (!roles || !Array.isArray(roles)) return null;

    if (roles.includes('super_admin')) return 'super_admin';
    if (roles.includes('admin')) return 'admin';
    if (roles.includes('approver')) return 'approver';
    if (roles.includes('admissions')) return 'admissions';
    if (roles.includes('editor')) return 'editor';
    if (roles.includes('parent')) return 'parent';
    
    return null;
  }

  async function checkExistingSession() {
    try {
      // Try staff/admin session first
      try {
        const response = await fetch(`${apiBase}/auth/me`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.ok && data.data?.user) {
            const role = getUserRole(data.data.user.roles);
            setUserRole(role);
            setUser(data.data.user);
            setLoading(false);
            return;
          }
        }
      } catch (_) {}

      // Try parent session
      try {
        const response = await fetch(`${apiBase}/admissions/me`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.ok && data.data?.user) {
            setUserRole('parent');
            setUser(data.data.user);
            setLoading(false);
            return;
          }
        }
      } catch (_) {}

      // No session found
      setUserRole(null);
      setUser(null);
    } catch (err) {
      console.error('Error checking session:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleLoginSuccess(result) {
    console.log('[LoginGateway] Login successful:', result);
    
    // Get role from user roles
    const role = getUserRole(result.user.roles);
    setUserRole(role);
    setUser(result.user);
  }

  async function handleLogout() {
    try {
      // Try auth logout first (for all admin roles)
      try {
        await fetch(`${apiBase}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (_) {}

      // Also try admissions logout
      try {
        await fetch(`${apiBase}/admissions/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (_) {}
    } catch (err) {
      console.error('Logout error:', err);
    }

    setUserRole(null);
    setUser(null);
  }

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: '#f5f7fa',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px',
            }}
          />
          <p style={{ color: '#999', fontSize: '14px' }}>Memuat...</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!userRole || !user) {
    return (
      <LoginForm
        onLoginSuccess={handleLoginSuccess}
        apiBase={apiBase}
      />
    );
  }

  // Route to appropriate dashboard based on role
  if (userRole === 'super_admin') {
    return (
      <SuperAdminDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
      />
    );
  }

  if (userRole === 'admin') {
    return (
      <WebsiteAdminDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
      />
    );
  }

  if (userRole === 'approver') {
    return (
      <PrincipalDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
      />
    );
  }

  if (userRole === 'admissions') {
    return (
      <AdmissionsAdminDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
      />
    );
  }

  if (userRole === 'parent') {
    return (
      <ParentDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
      />
    );
  }

  // Fallback for editor or other roles
  return (
    <StaffDashboard
      user={user}
      onLogout={handleLogout}
      apiBase={apiBase}
    />
  );
}

export default LoginGateway;
